<template>
  <div class="cve-info-panel" :class="{ 'dark-theme': isDark }" ref="panelRef">
    <div class="info-panel__header">
      <div class="header-title-row">
        <span v-if="info.type" class="node-type-badge" :class="info.type">{{ nodeTypeLabel }}</span>
        <span class="info-panel__title">{{ info.name }}</span>
      </div>
      <v-btn icon x-small @click="$emit('close')">
        <v-icon small>mdi-close</v-icon>
      </v-btn>
    </div>
    <div class="info-panel__content">
      <!-- Executive Summary - Always visible, non-collapsible -->
      <div v-if="showExecutiveSummary" class="info-section executive-summary">
        <div class="summary-grid">
          <div v-if="info.cves && info.cves.length" class="summary-item">
            <span class="summary-label">Connected CVEs</span>
            <div class="summary-chips">
              <v-tooltip
                v-for="cve in info.cves.slice(0, visibleChipsCount)"
                :key="cve"
                bottom
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    x-small
                    outlined
                    class="summary-chip chip-cve"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ cve }}
                  </v-chip>
                </template>
                <span>{{ getCveTooltip(cve) }}</span>
              </v-tooltip>
              <v-menu v-if="info.cves.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ info.cves.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All CVEs ({{ info.cves.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="c in info.cves" :key="c">{{ c }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
          <div v-if="info.cwes && info.cwes.length" class="summary-item">
            <span class="summary-label">Connected Weaknesses</span>
            <div class="summary-chips">
              <v-tooltip
                v-for="cwe in info.cwes.slice(0, visibleChipsCount)"
                :key="cwe"
                bottom
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    x-small
                    outlined
                    class="summary-chip chip-cwe"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ cwe }}
                  </v-chip>
                </template>
                <span>{{ getCweTooltip(cwe) }}</span>
              </v-tooltip>
              <v-menu v-if="info.cwes.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ info.cwes.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Weaknesses ({{ info.cwes.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="c in info.cwes" :key="c">{{ c }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
          <div v-if="info.capecs && info.capecs.length" class="summary-item">
            <span class="summary-label">Attack Patterns</span>
            <div class="summary-chips">
              <v-tooltip
                v-for="capec in info.capecs.slice(0, visibleChipsCount)"
                :key="capec"
                bottom
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    x-small
                    outlined
                    class="summary-chip chip-capec"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ capec }}
                  </v-chip>
                </template>
                <span>{{ getCapecTooltip(capec) }}</span>
              </v-tooltip>
              <v-menu v-if="info.capecs.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ info.capecs.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Attack Patterns ({{ info.capecs.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="c in info.capecs" :key="c">{{ c }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
          <div v-if="info.techniques && info.techniques.length" class="summary-item">
            <span class="summary-label">Techniques</span>
            <div class="summary-chips">
              <v-tooltip
                v-for="tech in info.techniques.slice(0, visibleChipsCount)"
                :key="tech"
                bottom
                max-width="400"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    x-small
                    outlined
                    class="summary-chip chip-ttp"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ tech }}
                  </v-chip>
                </template>
                <span>{{ getTechniqueTooltip(tech) }}</span>
              </v-tooltip>
              <v-menu v-if="info.techniques.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ info.techniques.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Techniques ({{ info.techniques.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="t in info.techniques" :key="t">{{ t }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
          <div v-if="info.connectedApts && info.connectedApts.length" class="summary-item">
            <span class="summary-label">Threat Actors</span>
            <div class="summary-chips">
              <v-chip
                v-for="apt in info.connectedApts.slice(0, visibleChipsCount)"
                :key="apt"
                x-small
                outlined
                class="summary-chip chip-apt"
              >
                {{ apt }}
              </v-chip>
              <v-menu v-if="info.connectedApts.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ info.connectedApts.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Threat Actors ({{ info.connectedApts.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="a in info.connectedApts" :key="a">{{ a }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>
      </div>

      <!-- CVE Details -->
      <div
        v-for="detail in cveDetails"
        :key="detail.cve"
        class="info-section"
      >
        <div class="info-section__header">
          <span class="cve-badge">{{ detail.cve }}</span>
          <v-chip
            v-if="detail.cvss"
            x-small
            :color="getCvssColor(detail.cvss)"
            class="cvss-chip"
          >
            CVSS {{ detail.cvss }}
          </v-chip>
        </div>

        <!-- Attack Vector - Why this connection exists -->
        <div class="attack-vector-section">
          <div class="attack-vector-header">
            <v-icon x-small color="info" class="section-icon">mdi-vector-polyline</v-icon>
            <span class="section-label">Attack Vector</span>
          </div>
          <div class="attack-vector-text" :class="{ 'no-data': !detail.attackVector, 'expandable': detail.attackVector && detail.attackVector.length > textMaxLength }">
            <template v-if="detail.attackVector">
              <span v-if="!expandedTexts[`attack-${detail.cve}`] && detail.attackVector.length > textMaxLength">
                {{ detail.attackVector.substring(0, textMaxLength) }}...
                <button class="show-more-btn" @click="toggleText(`attack-${detail.cve}`)">Show more</button>
              </span>
              <span v-else>
                {{ detail.attackVector }}
                <button v-if="detail.attackVector.length > textMaxLength" class="show-more-btn" @click="toggleText(`attack-${detail.cve}`)">Show less</button>
              </span>
            </template>
            <template v-else>No data available</template>
          </div>
        </div>

        <!-- Potential Risk / Impact -->
        <div class="risk-section">
          <div class="risk-header">
            <v-icon x-small color="warning" class="section-icon">mdi-alert-outline</v-icon>
            <span class="section-label">Potential Impact</span>
          </div>
          <div class="risk-text" :class="{ 'no-data': !detail.potentialRisk, 'expandable': detail.potentialRisk && detail.potentialRisk.length > textMaxLength }">
            <template v-if="detail.potentialRisk">
              <span v-if="!expandedTexts[`risk-${detail.cve}`] && detail.potentialRisk.length > textMaxLength">
                {{ detail.potentialRisk.substring(0, textMaxLength) }}...
                <button class="show-more-btn" @click="toggleText(`risk-${detail.cve}`)">Show more</button>
              </span>
              <span v-else>
                {{ detail.potentialRisk }}
                <button v-if="detail.potentialRisk.length > textMaxLength" class="show-more-btn" @click="toggleText(`risk-${detail.cve}`)">Show less</button>
              </span>
            </template>
            <template v-else>No data available</template>
          </div>
        </div>

        <div class="techniques-row">
          <span class="info-label">Techniques ({{ detail.techniques.length }}):</span>
          <div v-if="detail.techniques.length" class="technique-chips">
            <v-chip
              v-for="tech in detail.techniques.slice(0, detailChipsCount)"
              :key="tech"
              x-small
              outlined
              class="technique-chip"
            >
              {{ tech }}
            </v-chip>
            <v-menu v-if="detail.techniques.length > detailChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <span class="more-count clickable" v-bind="attrs" v-on="on">
                  +{{ detail.techniques.length - detailChipsCount }} more
                </span>
              </template>
              <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                <div class="tooltip-title">All Techniques ({{ detail.techniques.length }})</div>
                <ul class="tooltip-list">
                  <li v-for="t in detail.techniques" :key="t">{{ t }}</li>
                </ul>
              </v-card>
            </v-menu>
          </div>
          <span v-else class="no-data-text">No data</span>
        </div>

        <!-- CWE Weaknesses -->
        <div class="weaknesses-row">
          <span class="info-label">Weaknesses ({{ detail.cwes.length }}):</span>
          <div v-if="detail.cwes.length" class="weakness-chips">
            <v-chip
              v-for="cwe in detail.cwes.slice(0, detailChipsCount)"
              :key="cwe"
              x-small
              outlined
              class="weakness-chip"
            >
              {{ cwe }}
            </v-chip>
            <v-menu v-if="detail.cwes.length > detailChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <span class="more-count clickable" v-bind="attrs" v-on="on">
                  +{{ detail.cwes.length - detailChipsCount }} more
                </span>
              </template>
              <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                <div class="tooltip-title">All Weaknesses ({{ detail.cwes.length }})</div>
                <ul class="tooltip-list">
                  <li v-for="c in detail.cwes" :key="c">{{ c }}</li>
                </ul>
              </v-card>
            </v-menu>
          </div>
          <span v-else class="no-data-text">No data</span>
        </div>

        <!-- CAPEC Attack Patterns -->
        <div class="patterns-row">
          <span class="info-label">Attack Patterns ({{ detail.capecs.length }}):</span>
          <div v-if="detail.capecs.length" class="pattern-chips">
            <v-chip
              v-for="capec in detail.capecs.slice(0, detailChipsCount)"
              :key="capec"
              x-small
              outlined
              class="pattern-chip"
            >
              {{ capec }}
            </v-chip>
            <v-menu v-if="detail.capecs.length > detailChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <span class="more-count clickable" v-bind="attrs" v-on="on">
                  +{{ detail.capecs.length - detailChipsCount }} more
                </span>
              </template>
              <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                <div class="tooltip-title">All Attack Patterns ({{ detail.capecs.length }})</div>
                <ul class="tooltip-list">
                  <li v-for="c in detail.capecs" :key="c">{{ c }}</li>
                </ul>
              </v-card>
            </v-menu>
          </div>
          <span v-else class="no-data-text">No data</span>
        </div>
      </div>

      <!-- TTP Details Section -->
      <div class="info-section ttp-section">
        <div class="info-section__title collapsible-header" @click="toggleSection('ttp')">
          <div class="header-left">
            <v-icon x-small color="primary" class="mr-1">mdi-target</v-icon>
            Technique Details ({{ ttpDetails.length }})
          </div>
          <v-icon small class="collapse-icon" :class="{ 'rotated': !expandedSections.ttp }">
            mdi-chevron-down
          </v-icon>
        </div>
        <transition name="collapse">
          <div v-show="expandedSections.ttp" class="collapsible-content">
        <div v-if="ttpDetails.length === 0" class="no-data-message">No technique data available</div>
        <div
          v-for="ttp in ttpDetails.slice(0, 5)"
          :key="ttp.id"
          class="ttp-detail-item"
        >
          <div class="ttp-header">
            <span class="ttp-id">{{ ttp.id }}</span>
            <span v-if="ttp.name" class="ttp-name">{{ ttp.name }}</span>
          </div>

          <!-- Tactics - only show if has data -->
          <div v-if="ttp.tactics && ttp.tactics.length" class="ttp-row">
            <span class="ttp-row-label">Tactics:</span>
            <div class="ttp-tactics">
              <v-chip
                v-for="tactic in ttp.tactics"
                :key="tactic"
                x-small
                class="tactic-chip"
              >
                {{ formatTactic(tactic) }}
              </v-chip>
            </div>
          </div>

          <!-- Maturity - only show if has data -->
          <div v-if="ttp.maturity_level" class="ttp-row">
            <span class="ttp-row-label">Maturity:</span>
            <span class="maturity-badge" :class="getMaturityClass(ttp.maturity_level)">
              {{ ttp.maturity_level }}
            </span>
          </div>

          <!-- Years in use - only show if has data -->
          <div v-if="ttp.years_using" class="ttp-row">
            <span class="ttp-row-label">Years in use:</span>
            <span class="years-badge">{{ ttp.years_using }} years</span>
          </div>

          <!-- First observed - only show if has data -->
          <div v-if="ttp.first_observe" class="ttp-row">
            <span class="ttp-row-label">First observed:</span>
            <span class="date-text">{{ formatDate(ttp.first_observe) }}</span>
          </div>

          <!-- Last observed - only show if has data -->
          <div v-if="ttp.last_observe" class="ttp-row">
            <span class="ttp-row-label">Last observed:</span>
            <span class="date-text">{{ formatDate(ttp.last_observe) }}</span>
          </div>

          <!-- Description - only show if has data -->
          <template v-if="ttp.description">
            <div class="ttp-row">
              <span class="ttp-row-label">Description:</span>
            </div>
            <div class="ttp-description expandable">
              <span v-if="!expandedTexts[`ttp-${ttp.id}`] && cleanDescription(ttp.description).length > descriptionMaxLength">
                {{ cleanDescription(ttp.description).substring(0, descriptionMaxLength) }}...
                <button class="show-more-btn" @click="toggleText(`ttp-${ttp.id}`)">Show more</button>
              </span>
              <span v-else>
                {{ cleanDescription(ttp.description) }}
                <button v-if="cleanDescription(ttp.description).length > descriptionMaxLength" class="show-more-btn" @click="toggleText(`ttp-${ttp.id}`)">Show less</button>
              </span>
            </div>
          </template>

          <!-- D3FEND Countermeasures - only show if has data -->
          <div v-if="ttp.d3fends && ttp.d3fends.length" class="ttp-row">
            <span class="ttp-row-label">D3FEND Countermeasures:</span>
            <div class="ttp-d3fends">
              <v-chip
                v-for="d3f in ttp.d3fends.slice(0, 3)"
                :key="d3f"
                x-small
                outlined
                class="d3fend-chip"
              >
                {{ d3f }}
              </v-chip>
            </div>
          </div>
        </div>
        <div v-if="ttpDetails.length > 5" class="more-ttps">
          +{{ ttpDetails.length - 5 }} more techniques
        </div>
          </div>
        </transition>
      </div>

      <!-- Related APT Groups - Enhanced -->
      <div class="info-section apt-section">
        <div class="info-section__title collapsible-header" @click="toggleSection('apt')">
          <div class="header-left">
            <v-icon x-small color="error" class="mr-1">mdi-account-group</v-icon>
            Related Threat Actors ({{ relatedAptGroups.length }})
          </div>
          <v-icon small class="collapse-icon" :class="{ 'rotated': !expandedSections.apt }">
            mdi-chevron-down
          </v-icon>
        </div>
        <transition name="collapse">
          <div v-show="expandedSections.apt" class="collapsible-content">
        <div v-if="relatedAptGroups.length === 0" class="no-data-message">No threat actor data available</div>
        <div
          v-for="group in relatedAptGroups.slice(0, 5)"
          :key="getGroupName(group)"
          class="apt-detail-item"
        >
          <div class="apt-header">
            <div class="apt-name">
              <span
                class="apt-color"
                :style="{ backgroundColor: group.color || '#666' }"
              />
              {{ getGroupName(group) }}
              <span v-if="group.group_id" class="apt-id">({{ group.group_id }})</span>
            </div>
            <div v-if="group.operational_status" class="apt-status" :class="getStatusClass(group.operational_status)">
              {{ group.operational_status }}
            </div>
          </div>

          <!-- Aliases - only show if has data -->
          <div v-if="group.aliases && group.aliases.length" class="apt-row">
            <span class="apt-row-label">Aliases:</span>
            <span class="alias-list">
              {{ group.aliases.slice(0, visibleChipsCount).join(', ') }}
              <v-menu v-if="group.aliases.length > visibleChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">+{{ group.aliases.length - visibleChipsCount }}</span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Aliases ({{ group.aliases.length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="a in group.aliases" :key="a">{{ a }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </span>
          </div>

          <!-- Description - only show if has data -->
          <template v-if="group.description">
            <div class="apt-row">
              <span class="apt-row-label">Description:</span>
            </div>
            <div class="apt-description expandable">
              <span v-if="!expandedTexts[`apt-${getGroupName(group)}`] && cleanDescription(group.description).length > descriptionMaxLength">
                {{ cleanDescription(group.description).substring(0, descriptionMaxLength) }}...
                <button class="show-more-btn" @click="toggleText(`apt-${getGroupName(group)}`)">Show more</button>
              </span>
              <span v-else>
                {{ cleanDescription(group.description) }}
                <button v-if="cleanDescription(group.description).length > descriptionMaxLength" class="show-more-btn" @click="toggleText(`apt-${getGroupName(group)}`)">Show less</button>
              </span>
            </div>
          </template>

          <!-- Stats Row - always show, uses '-' for missing values -->
          <div class="apt-stats-row">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div class="apt-stat" v-bind="attrs" v-on="on">
                  <span class="apt-stat-value">{{ group.total_techniques || '-' }}</span>
                  <span class="apt-stat-label">TTPs</span>
                </div>
              </template>
              <span>Total number of Tactics, Techniques, and Procedures used by this threat actor group</span>
            </v-tooltip>
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div class="apt-stat" v-bind="attrs" v-on="on">
                  <span class="apt-stat-value">{{ group.relevancy ? Math.round(group.relevancy * 100) + '%' : '-' }}</span>
                  <span class="apt-stat-label">Relevancy</span>
                </div>
              </template>
              <span>Percentage of overlap between this group's TTPs and the vulnerabilities found in your environment</span>
            </v-tooltip>
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div class="apt-stat" v-bind="attrs" v-on="on">
                  <span class="apt-stat-value">{{ group.avg_technique_maturity_years ? group.avg_technique_maturity_years + 'y' : '-' }}</span>
                  <span class="apt-stat-label">Avg Maturity</span>
                </div>
              </template>
              <span>Average number of years this group has been using their techniques, indicating their level of experience</span>
            </v-tooltip>
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <div class="apt-stat" v-bind="attrs" v-on="on">
                  <span class="apt-stat-value">{{ group.months_since_activity !== undefined ? group.months_since_activity + 'm' : '-' }}</span>
                  <span class="apt-stat-label">Last Active</span>
                </div>
              </template>
              <span>Number of months since this threat actor group was last observed in active campaigns</span>
            </v-tooltip>
          </div>

          <!-- Rank - only show if has data -->
          <div v-if="group.rank" class="apt-row">
            <span class="apt-row-label">Rank:</span>
            <span class="rank-badge">{{ group.rank }}</span>
          </div>

          <!-- Recent updates - only show if has data -->
          <div v-if="group.recent_updates_12mo !== undefined" class="apt-row">
            <span class="apt-row-label">Recent updates (12mo):</span>
            <span class="recent-updates-value">{{ group.recent_updates_12mo }}</span>
          </div>

          <!-- Targeting Sectors - only show if has data -->
          <div v-if="getUniqueSectors(group).length" class="apt-targeting">
            <span class="targeting-label">Target Sectors:</span>
            <div class="targeting-chips">
              <v-chip
                v-for="sector in getUniqueSectors(group).slice(0, detailChipsCount)"
                :key="sector"
                x-small
                class="sector-chip"
              >
                {{ sector }}
              </v-chip>
              <v-menu v-if="getUniqueSectors(group).length > detailChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">
                    +{{ getUniqueSectors(group).length - detailChipsCount }}
                  </span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Target Sectors ({{ getUniqueSectors(group).length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="s in getUniqueSectors(group)" :key="s">{{ s }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>

          <!-- Targeting Regions - only show if has data -->
          <div v-if="getUniqueRegions(group).length" class="apt-targeting">
            <span class="targeting-label">Target Regions:</span>
            <div class="targeting-chips">
              <v-chip
                v-for="region in getUniqueRegions(group).slice(0, detailChipsCount)"
                :key="region"
                x-small
                class="region-chip"
              >
                {{ region }}
              </v-chip>
              <v-menu v-if="getUniqueRegions(group).length > detailChipsCount" open-on-hover offset-y :close-on-content-click="false" max-width="350">
                <template v-slot:activator="{ on, attrs }">
                  <span class="more-count clickable" v-bind="attrs" v-on="on">
                    +{{ getUniqueRegions(group).length - detailChipsCount }}
                  </span>
                </template>
                <v-card class="tooltip-card" :class="{ 'dark-theme': isDark }">
                  <div class="tooltip-title">All Target Regions ({{ getUniqueRegions(group).length }})</div>
                  <ul class="tooltip-list">
                    <li v-for="r in getUniqueRegions(group)" :key="r">{{ r }}</li>
                  </ul>
                </v-card>
              </v-menu>
            </div>
          </div>
        </div>
          </div>
        </transition>
      </div>

      <!-- Indicators of Compromise (IoC) Section - Coming Soon -->
      <div class="info-section ioc-section">
        <div class="info-section__title collapsible-header" @click="toggleSection('ioc')">
          <div class="header-left">
            <v-icon x-small color="success" class="mr-1">mdi-shield-search</v-icon>
            <span class="ioc-title-text">Indicators of Compromise (IoC)</span>
            <v-tooltip bottom max-width="350">
              <template v-slot:activator="{ on, attrs }">
                <v-icon x-small class="ioc-info-icon" v-bind="attrs" v-on="on">mdi-information-outline</v-icon>
              </template>
              <div class="ioc-tooltip-content">
                <div class="ioc-tooltip-title">What are Indicators of Compromise (IoC)?</div>
                <p>Indicators of compromise (IoCs) are information about a specific security breach that can help security teams determine if an attack has taken place.</p>
                <p>This data can include details about the attack, such as the type of malware used, the IP addresses involved, and other technical details.</p>
                <p>Fresh and accurate threat intelligence including specific IP addresses, sinkholes, and C&C information related to threat actor groups based on live telemetry data.</p>
              </div>
            </v-tooltip>
          </div>
          <v-icon small class="collapse-icon" :class="{ 'rotated': !expandedSections.ioc }">
            mdi-chevron-down
          </v-icon>
        </div>
        <transition name="collapse">
          <div v-show="expandedSections.ioc" class="collapsible-content">
            <div class="ioc-placeholder">
              <div class="ioc-premium-badge">
                <v-icon small class="mr-1">mdi-star</v-icon>
                Premium Feature
              </div>
              <v-icon color="success" size="32" class="ioc-placeholder-icon">mdi-shield-lock-outline</v-icon>
              <div class="ioc-placeholder-title">
                Unlock IoC Intelligence
              </div>
              <div class="ioc-placeholder-text">
                Access IP addresses, file hashes, domains, and other threat indicators associated with this vulnerability.
              </div>
              <a href="mailto:sales@cynergy.com" class="ioc-contact-link">
                <v-icon x-small class="mr-1">mdi-email-outline</v-icon>
                Contact Sales
              </a>
            </div>
          </div>
        </transition>
      </div>

      <!-- Summary Stats -->
      <div class="info-section stats-section">
        <div class="stat-item">
          <span class="stat-value">{{ info.cves ? info.cves.length : 0 }}</span>
          <span class="stat-label">CVEs</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ info.techniques ? info.techniques.length : 0 }}</span>
          <span class="stat-label">Techniques</span>
        </div>
        <div class="stat-item">
          <span class="stat-value">{{ relatedAptGroups.length }}</span>
          <span class="stat-label">APT Groups</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CVEInfoPanel',
  props: {
    info: {
      type: Object,
      required: true,
    },
    attackPathsMap: {
      type: Object,
      default: () => ({}),
    },
    aptGroups: {
      type: Array,
      default: () => [],
    },
    isDark: {
      type: Boolean,
      default: false,
    },
    insightsData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      expandedSections: {
        ttp: false,
        apt: false,
        ioc: false,
      },
      expandedTexts: {},
      panelWidth: 360,
    };
  },
  computed: {
    nodeTypeLabel() {
      const labels = {
        org: 'Organization',
        asset: 'Asset',
        cve: 'CVE',
        cwe: 'CWE',
        capec: 'CAPEC',
        ttp: 'TTP',
        apt: 'APT Group',
        technique: 'Technique',
      };
      return labels[this.info?.type] || '';
    },
    // Dynamic chip count based on panel width
    visibleChipsCount() {
      if (this.panelWidth >= 500) return 8;
      if (this.panelWidth >= 400) return 6;
      if (this.panelWidth >= 350) return 4;
      return 3;
    },
    detailChipsCount() {
      if (this.panelWidth >= 500) return 10;
      if (this.panelWidth >= 400) return 8;
      if (this.panelWidth >= 350) return 6;
      return 4;
    },
    textMaxLength() {
      if (this.panelWidth >= 500) return 300;
      if (this.panelWidth >= 400) return 200;
      return 150;
    },
    descriptionMaxLength() {
      if (this.panelWidth >= 500) return 250;
      if (this.panelWidth >= 400) return 180;
      return 120;
    },
    showExecutiveSummary() {
      const hasConnections = (this.info?.cves?.length > 0) ||
        (this.info?.cwes?.length > 0) ||
        (this.info?.capecs?.length > 0) ||
        (this.info?.techniques?.length > 0) ||
        (this.info?.connectedApts?.length > 0);
      return hasConnections;
    },
    cveDetails() {
      if (!this.info?.cves?.length) {
        return [];
      }
      return this.info.cves.map(cve => {
        const path = this.attackPathsMap[cve] || {};
        return {
          cve,
          attackVector: path.attackVector || null,
          potentialRisk: path.potentialRisk || null,
          cvss: path.cvss || null,
          cwes: path.cwes || [],
          capecs: path.capecs || [],
          techniques: path.techniques || [],
        };
      });
    },
    ttpDetails() {
      const ttps = [];
      const seen = new Set();
      const techniques = this.info?.techniques || [];
      // Build a map of TTP data from multiple sources
      const ttpDataMap = new Map();
      // Source 1: APT groups' overlap_ttps
      const aptGroups = this.aptGroups || [];
      aptGroups.forEach(group => {
        const groupTtps = group.TTPs || group.ttps || group.overlap_ttps || [];
        groupTtps.forEach(t => {
          if (typeof t === 'object' && t.id) {
            ttpDataMap.set(t.id, t);
          }
        });
      });
      // Source 2: If this is an APT node, use its own overlap_ttps data
      if (this.info?.type === 'apt' && this.info?.nodeData) {
        const nodeTtps = this.info.nodeData.TTPs || this.info.nodeData.ttps || this.info.nodeData.overlap_ttps || [];
        nodeTtps.forEach(t => {
          if (typeof t === 'object' && t.id) {
            ttpDataMap.set(t.id, t);
          }
        });
      }
      techniques.forEach(techId => {
        if (seen.has(techId)) return;
        seen.add(techId);
        const ttpData = ttpDataMap.get(techId) || null;
        ttps.push({
          id: techId,
          name: ttpData?.name || null,
          description: ttpData?.description || null,
          tactics: ttpData?.tactics || [],
          maturity_level: ttpData?.maturity_level || null,
          years_using: ttpData?.years_using || null,
          first_observe: ttpData?.first_observe || null,
          last_observe: ttpData?.last_observe || null,
          d3fends: ttpData?.d3fends || [],
          months_since_update: ttpData?.months_since_update || null,
        });
      });
      return ttps;
    },
    relatedAptGroups() {
      if (!this.aptGroups?.length) {
        return [];
      }
      if (!this.info?.techniques?.length) {
        return this.aptGroups.slice(0, 5);
      }
      const techSet = new Set(this.info.techniques);
      const filtered = this.aptGroups.filter(group => {
        const groupTtps = this.getGroupTtps(group);
        return groupTtps.some(ttp => techSet.has(ttp));
      });
      return filtered.length > 0 ? filtered.slice(0, 5) : this.aptGroups.slice(0, 5);
    },
  },
  mounted() {
    this.updatePanelWidth();
    window.addEventListener('resize', this.updatePanelWidth);
    // Use ResizeObserver if available for more accurate tracking
    if (typeof ResizeObserver !== 'undefined' && this.$refs.panelRef) {
      this.resizeObserver = new ResizeObserver(this.updatePanelWidth);
      this.resizeObserver.observe(this.$refs.panelRef);
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePanelWidth);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  methods: {
    getCveTooltip(cve) {
      // Get CVE details from attackPathsMap
      const cveData = this.attackPathsMap[cve];
      if (cveData && cveData.potentialRisk) {
        return cveData.potentialRisk;
      }
      return `${cve} - Click for more details`;
    },
    getCweTooltip(cwe) {
      // Try to find CWE details from the attack paths
      const cveWithThisCwe = Object.values(this.attackPathsMap).find(path => path.cwes && path.cwes.includes(cwe));

      if (cveWithThisCwe) {
        // Look for CWE data in the insights
        const orgCves = this.insightsData?.organization?.org_cves || [];
        const cveWithCweData = orgCves.find(cveItem => {
          if (cveItem.cwes && Array.isArray(cveItem.cwes)) {
            const cweData = cveItem.cwes.find(c => c.name === cwe);
            return cweData && cweData.description;
          }
          return false;
        });

        if (cveWithCweData) {
          const cweData = cveWithCweData.cwes.find(c => c.name === cwe);
          if (cweData && cweData.description) {
            return `${cwe}: ${cweData.description}`;
          }
        }
      }
      return `${cwe} - Common Weakness Enumeration`;
    },
    getCapecTooltip(capec) {
      // Try to find CAPEC details from the insights
      const orgCves = this.insightsData?.organization?.org_cves || [];
      let result = null;
      orgCves.forEach(cveItem => {
        if (result) return;
        if (cveItem.cwes && Array.isArray(cveItem.cwes)) {
          cveItem.cwes.forEach(cweData => {
            if (result) return;
            if (cweData.capec && Array.isArray(cweData.capec)) {
              const capecData = cweData.capec.find(c => (c.id || c.name) === capec);
              if (capecData) {
                if (capecData.description && capecData.description.trim()) {
                  result = `${capec}: ${capecData.description}`;
                } else if (capecData.name && capecData.name.trim() && capecData.name !== capec) {
                  result = `${capec}: ${capecData.name}`;
                }
              }
            }
          });
        }
      });
      return result || `${capec} - Attack pattern details not available`;
    },
    getTechniqueTooltip(tech) {
      // Try to find technique details from APT groups or insights
      const orgCves = this.insightsData?.organization?.org_cves || [];
      let result = null;
      orgCves.forEach(cveItem => {
        if (result) return;
        if (cveItem.cwes && Array.isArray(cveItem.cwes)) {
          cveItem.cwes.forEach(cweData => {
            if (result) return;
            if (cweData.capec && Array.isArray(cweData.capec)) {
              cweData.capec.forEach(capecData => {
                if (result) return;
                if (capecData.ttps && Array.isArray(capecData.ttps)) {
                  const ttpData = capecData.ttps.find(t => t.id === tech);
                  if (ttpData) {
                    if (ttpData.description) {
                      result = `${tech}: ${ttpData.description}`;
                    } else if (ttpData.name) {
                      result = `${tech}: ${ttpData.name}`;
                    }
                  }
                }
              });
            }
          });
        }
      });
      return result || `${tech} - MITRE ATT&CK Technique`;
    },
    updatePanelWidth() {
      if (this.$refs.panelRef) {
        this.panelWidth = this.$refs.panelRef.offsetWidth;
      }
    },
    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section];
    },
    toggleText(key) {
      this.$set(this.expandedTexts, key, !this.expandedTexts[key]);
    },
    getGroupName(group) {
      return group.name || group['APT name'] || group.APT_group || 'Unknown';
    },
    getGroupTtps(group) {
      const ttps = group.TTPs || group.ttps || group.overlap_ttps || [];
      return ttps.map(t => {
        if (typeof t === 'string') {
          const match = t.match(/^(T\d+(?:\.\d+)?)/);
          return match ? match[1] : t;
        }
        return t.id || t;
      });
    },
    getCvssColor(cvss) {
      const score = parseFloat(cvss);
      if (score >= 9.0) return '#d32f2f';
      if (score >= 7.0) return '#f57c00';
      if (score >= 4.0) return '#fbc02d';
      return '#388e3c';
    },
    formatTactic(tactic) {
      return tactic.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    },
    getMaturityClass(level) {
      if (!level) return '';
      if (level.includes('Highly Mature')) return 'mature';
      if (level.includes('Established')) return 'established';
      return 'recent';
    },
    getStatusClass(status) {
      if (!status) return '';
      if (status.toLowerCase().includes('active')) return 'active';
      return 'inactive';
    },
    truncateText(text, maxLength) {
      if (!text || text.length <= maxLength) return text;
      return `${text.substring(0, maxLength)}...`;
    },
    cleanDescription(desc) {
      if (!desc) return '';
      return desc.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    },
    getUniqueSectors(group) {
      const sectors = group.targeting_sectors || [];
      return [...new Set(sectors.map(s => s.name || s))];
    },
    getUniqueRegions(group) {
      const regions = group.targeting_regions || [];
      return [...new Set(regions.map(r => r.name || r))];
    },
    formatDate(dateStr) {
      if (!dateStr) return null;
      try {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      } catch {
        return dateStr;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

// Use design system colors - unified blue theme
// Light mode uses $info-500 (#2459d5) as accent
// Dark mode uses $node-org-dark (#00f5ff) for glow effects

.cve-info-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: $gray-50;
  color: $gray-900;

  &.dark-theme {
    background: linear-gradient(180deg, $graph-bg-dark 0%, darken($graph-bg-dark, 2%) 100%);
    color: $dark-text-primary;
  }
}

.info-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $space-4;
  background: $white;
  border-bottom: $border-width-thin solid $gray-200;
  position: sticky;
  top: 0;
  z-index: $z-sticky;

  .dark-theme & {
    background: rgba($info-500, 0.12);
    border-bottom-color: rgba($node-org-dark, 0.25);
    box-shadow: 0 $space-1 $space-6 rgba($black, 0.4), 0 0 $space-10 rgba($node-org-dark, 0.2);

    .v-btn {
      color: $dark-text-primary;
      border-radius: 50%;

      &:hover {
        background: rgba($node-org-dark, 0.15);
      }

      &::before {
        border-radius: 50%;
      }
    }
  }
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: $space-2;
  flex: 1;
  min-width: 0;
}

.node-type-badge {
  font-size: $font-size-sm; // Increased from xs
  font-weight: 600;
  text-transform: uppercase;
  padding: $space-0-5 $space-2;
  border-radius: $radius-lg;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  background: $info-50;
  color: $info-500;
  border: $border-width-thin solid rgba($info-500, 0.25);

  .dark-theme & {
    background: rgba($node-org-dark, 0.2);
    color: $node-org-dark;
    border-color: rgba($node-org-dark, 0.4);
    box-shadow: 0 0 $space-4 rgba($node-org-dark, 0.5), 0 0 $space-8 rgba($node-org-dark, 0.3);
  }
}

.info-panel__title {
  font-size: $font-size-lg; // Increased from base
  font-weight: 600;
  color: $gray-900;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;

  .dark-theme & {
    color: $dark-text-primary;
  }
}

.info-panel__content {
  padding: $space-4;
  overflow-y: auto;
  flex: 1;
  background: transparent;
}

// Executive Summary Styles
.executive-summary {
  background: $white;
  border: $border-width-thin solid $gray-200;

  .dark-theme & {
    background: rgba($info-500, 0.1);
    border-color: rgba($node-org-dark, 0.25);
    box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.15);
  }
}

.summary-grid {
  display: flex;
  flex-direction: column;
  gap: $space-3;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.summary-label {
  font-size: $font-size-base; // Increased from sm
  font-weight: 600;
  color: $gray-700;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  .dark-theme & {
    color: $dark-text-secondary;
  }
}

.summary-chips {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
  align-items: center;
}

.summary-chip {
  font-size: $font-size-sm; // Increased from xs
  height: $space-5;
  background: $info-50;
  color: $info-500;
  border-color: rgba($info-500, 0.3);

  .dark-theme & {
    background: rgba($node-org-dark, 0.15);
    color: $node-org-dark;
    border-color: rgba($node-org-dark, 0.35);
    box-shadow: 0 0 $space-2 rgba($node-org-dark, 0.3);
  }

  // All chip variants use the same unified blue color scheme
  &.chip-cve,
  &.chip-cwe,
  &.chip-capec,
  &.chip-ttp,
  &.chip-apt {
    background: $info-50;
    color: $info-500;
    border-color: rgba($info-500, 0.3);

    .dark-theme & {
      background: rgba($node-org-dark, 0.15);
      color: $node-org-dark;
      border-color: rgba($node-org-dark, 0.35);
      box-shadow: 0 0 $space-2 rgba($node-org-dark, 0.3);
    }
  }
}

.info-section {
  background: $white;
  border-radius: $radius-lg;
  padding: $space-3;
  margin-bottom: $space-3;
  border: $border-width-thin solid $gray-200;

  .dark-theme & {
    background: rgba($info-500, 0.1);
    border-color: rgba($node-org-dark, 0.25);
    box-shadow: 0 0 $space-5 rgba($node-org-dark, 0.12);
  }

  &__header {
    display: flex;
    align-items: center;
    gap: $space-2;
    margin-bottom: $space-3;
  }

  &__title {
    font-size: $font-size-sm;
    font-weight: 700;
    color: $gray-700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    margin-bottom: $space-3;
    letter-spacing: 0.5px;

    .dark-theme & {
      color: $dark-text-secondary;
    }

    &.collapsible-header {
      cursor: pointer;
      justify-content: space-between;
      align-items: center;
      user-select: none;
      padding: $space-3;
      margin: -#{$space-3};
      border-radius: $radius-lg;
      transition: background $transition-duration-fast, box-shadow $transition-duration-fast;
      min-height: $space-12;

      &:hover {
        background: rgba($info-500, 0.08);
      }

      .header-left {
        display: flex;
        align-items: center;
      }

      .v-icon {
        color: $info-500;
      }
    }
  }
}

// Dark theme overrides for scoped styles
.dark-theme.cve-info-panel .info-section__title.collapsible-header {
  &:hover {
    background: rgba($node-org-dark, 0.15);
    box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.3), inset 0 0 $space-4 rgba($node-org-dark, 0.1);
  }

  .v-icon {
    color: $node-org-dark;
  }
}

.collapse-icon {
  transition: transform $transition-duration-normal ease;

  &.rotated {
    transform: rotate(-90deg);
  }
}

.collapsible-content {
  padding-top: $space-4;
  margin-top: $space-3;
}

.collapse-enter-active,
.collapse-leave-active {
  transition: all $transition-duration-normal ease;
  overflow: hidden;
}

.collapse-enter,
.collapse-leave-to {
  opacity: 0;
  max-height: 0;
}

.collapse-enter-to,
.collapse-leave {
  opacity: 1;
  max-height: 2000px;
}

.cve-badge {
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-size: $font-size-base; // Increased from sm
  font-weight: 600;
  color: $info-500;
  background: $info-50;
  padding: $space-1 $space-2;
  border-radius: $radius-md;
  border: $border-width-thin solid rgba($info-500, 0.25);

  .dark-theme & {
    color: $node-org-dark;
    background: rgba($node-org-dark, 0.2);
    border-color: rgba($node-org-dark, 0.35);
    box-shadow: 0 0 $space-3 rgba($node-org-dark, 0.4);
  }
}

.cvss-chip {
  color: $white;
  font-weight: 600;
}

.no-data-text {
  font-size: $font-size-base; // Increased from sm
  color: $gray-500;
  font-style: italic;
}

.no-data-message {
  font-size: $font-size-base; // Increased from sm
  color: $gray-500;
  font-style: italic;
  text-align: center;
  padding: $space-3;
}

.info-label {
  color: $gray-700;
  font-weight: 600;
  margin-right: $space-1;
  white-space: nowrap;
  font-size: $font-size-sm;

  .dark-theme & {
    color: $dark-text-secondary;
  }
}

.attack-vector-section,
.risk-section {
  margin-bottom: $space-3;
}

.attack-vector-header,
.risk-header {
  display: flex;
  align-items: center;
  margin-bottom: $space-1;
}

.section-icon {
  margin-right: $space-1;
  flex-shrink: 0;
  color: $info-500;

  .dark-theme & {
    color: $node-org-dark;
  }
}

.section-label {
  font-size: $font-size-base; // Increased from sm
  font-weight: 600;
  color: $gray-600;
  text-transform: uppercase;

  .dark-theme & {
    color: $dark-text-secondary;
  }
}

.attack-vector-text {
  font-size: $font-size-base; // Increased from sm
  color: $gray-900;
  line-height: $line-height-normal;
  padding: $space-2;
  background: $gray-50;
  border-radius: $radius-md;
  border-left: 3px solid $info-500;

  .dark-theme & {
    background: rgba($node-org-dark, 0.1);
    border-left-color: $node-org-dark;
    color: $dark-text-primary;
    box-shadow: inset 0 0 $space-4 rgba($node-org-dark, 0.1);
  }

  &.no-data {
    background: $gray-100;
    border-left-color: $gray-300;
    color: $gray-500;
    font-style: italic;

    .dark-theme & {
      background: rgba($info-500, 0.05);
      border-left-color: rgba($node-org-dark, 0.3);
      color: $dark-text-secondary;
    }
  }
}

.risk-text {
  font-size: $font-size-base; // Increased from sm
  color: $gray-900;
  line-height: $line-height-normal;
  padding: $space-2;
  background: $gray-50;
  border-radius: $radius-md;
  border-left: 3px solid $info-500;

  .dark-theme & {
    background: rgba($node-org-dark, 0.1);
    border-left-color: $node-org-dark;
    color: $dark-text-primary;
    box-shadow: inset 0 0 $space-4 rgba($node-org-dark, 0.1);
  }

  &.no-data {
    background: $gray-100;
    border-left-color: $gray-300;
    color: $gray-500;
    font-style: italic;

    .dark-theme & {
      background: rgba($info-500, 0.05);
      border-left-color: rgba($node-org-dark, 0.3);
      color: $dark-text-secondary;
    }
  }
}

.techniques-row,
.weaknesses-row,
.patterns-row {
  margin-top: $space-2;

  .info-label {
    display: block;
    margin-bottom: $space-2;
    font-size: $font-size-base; // Increased from sm
    color: $gray-600;

    .dark-theme & {
      color: $dark-text-secondary;
    }
  }
}

.technique-chips,
.weakness-chips,
.pattern-chips {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
  align-items: center;
}

.technique-chip,
.weakness-chip,
.pattern-chip {
  font-size: $font-size-sm; // Increased from xs
  height: $space-5;
  background: $info-50;
  color: $info-500;
  border-color: rgba($info-500, 0.25);

  .dark-theme & {
    background: rgba($node-org-dark, 0.15);
    color: $node-org-dark;
    border-color: rgba($node-org-dark, 0.3);
    box-shadow: 0 0 $space-1 rgba($node-org-dark, 0.3);
  }
}

.more-count {
  font-size: $font-size-sm; // Increased from xs
  color: $gray-600;
  font-style: italic;

  .dark-theme & {
    color: $dark-text-secondary;
  }

  &.clickable {
    cursor: pointer;
    text-decoration: underline;
    text-decoration-style: dotted;

    &:hover {
      color: $info-500;

      .dark-theme & {
        color: $node-org-dark;
      }
    }
  }
}

// Show more button
.show-more-btn {
  background: none;
  border: none;
  color: $info-500;
  font-size: $font-size-sm; // Increased from xs
  cursor: pointer;
  padding: 0;
  margin-left: $space-1;
  text-decoration: underline;
  font-weight: 500;

  .dark-theme & {
    color: $node-org-dark;
  }

  &:hover {
    opacity: 0.8;
  }
}

.expandable {
  word-break: break-word;
}

// IoC Section Styles
.ioc-section {
  background: $white;
  border-color: $gray-200;

  .dark-theme & {
    background: rgba($info-500, 0.1);
    border-color: rgba($node-org-dark, 0.25);
    box-shadow: 0 0 $space-5 rgba($node-org-dark, 0.12);
  }
}

.ioc-title-text {
  font-size: $font-size-sm;
  font-weight: 600;
  color: $gray-600;
  text-transform: uppercase;

  .dark-theme & {
    color: $dark-text-secondary;
  }
}

.ioc-info-icon {
  color: $gray-500;
  margin-left: $space-1;
  cursor: pointer;
  opacity: 0.7;
  transition: opacity $transition-duration-fast ease;

  &:hover {
    opacity: 1;
    color: $info-500;

    .dark-theme & {
      color: $node-org-dark;
    }
  }
}

.ioc-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: $space-5;
  text-align: center;
  background: $gray-50;
  border-radius: $radius-lg;
  border: $border-width-thin solid $gray-200;

  .dark-theme & {
    background: rgba($node-org-dark, 0.08);
    border-color: rgba($node-org-dark, 0.25);
  }
}

.ioc-premium-badge {
  display: inline-flex;
  align-items: center;
  font-size: $font-size-sm; // Increased from 2xs
  font-weight: 600;
  text-transform: uppercase;
  padding: $space-1 $space-2;
  border-radius: $radius-xl;
  background: $info-500;
  color: $white;
  letter-spacing: 0.5px;
  margin-bottom: $space-3;

  .dark-theme & {
    background: $node-org-dark;
    color: $graph-bg-dark;
    box-shadow: 0 0 $space-4 rgba($node-org-dark, 0.6);
  }

  .v-icon {
    color: $white;

    .dark-theme & {
      color: $graph-bg-dark;
    }
  }
}

.ioc-placeholder-icon {
  opacity: 0.5;
  margin-bottom: $space-2;
  color: $info-500;

  .dark-theme & {
    color: $node-org-dark;
  }
}

.ioc-placeholder-title {
  font-size: $font-size-base;
  font-weight: 600;
  color: $gray-900;
  margin-bottom: $space-1;

  .dark-theme & {
    color: $dark-text-primary;
  }
}

.ioc-placeholder-text {
  font-size: $font-size-sm;
  color: $gray-600;
  line-height: $line-height-normal;
  max-width: 280px;
  margin-bottom: $space-3;

  .dark-theme & {
    color: $dark-text-secondary;
  }
}

.ioc-contact-link {
  display: inline-flex;
  align-items: center;
  font-size: $font-size-sm;
  font-weight: 500;
  color: $info-500;
  text-decoration: none;
  padding: $space-1-5 $space-3;
  border-radius: $radius-md;
  border: $border-width-thin solid $info-500;
  transition: all $transition-duration-fast ease;

  .dark-theme & {
    color: $node-org-dark;
    border-color: rgba($node-org-dark, 0.5);
  }

  &:hover {
    background: $info-500;
    color: $white;

    .dark-theme & {
      background: $node-org-dark;
      color: $graph-bg-dark;
      box-shadow: 0 0 $space-5 rgba($node-org-dark, 0.6);
    }

    .v-icon {
      color: $white;

      .dark-theme & {
        color: $graph-bg-dark;
      }
    }
  }

  .v-icon {
    color: $info-500;

    .dark-theme & {
      color: $node-org-dark;
    }
  }
}

.ioc-tooltip-content {
  padding: $space-1;

  .ioc-tooltip-title {
    font-weight: 600;
    font-size: $font-size-sm;
    margin-bottom: $space-2;
    color: $info-500;
  }

  p {
    font-size: $font-size-sm;
    line-height: $line-height-normal;
    margin-bottom: $space-2;

    &:last-child {
      margin-bottom: 0;
    }
  }
}
</style>

<style lang="scss">
@import '~@/assets/styles/design-system';

// Use design system colors for global styles

// Override Vuetify chip colors globally within the panel
#app .cve-info-panel .v-chip.v-chip {
  background: $info-50;
  color: $info-500;
  border-color: rgba($info-500, 0.3);
}

#app .cve-info-panel.dark-theme .v-chip.v-chip {
  background: rgba($node-org-dark, 0.15);
  color: $node-org-dark;
  border-color: rgba($node-org-dark, 0.35);
  box-shadow: 0 0 $space-2 rgba($node-org-dark, 0.3);
}

// CVSS chip keeps its severity colors
#app .cve-info-panel .cvss-chip.v-chip {
  color: $white;
}

#app .tooltip-card {
  padding: $space-3;
  max-height: 400px;
  overflow-y: auto;
  background: $white;
  color: $gray-900;

  &.dark-theme {
    background: $dark-bg-primary;
    color: $dark-text-primary;
    border: $border-width-thin solid rgba($node-org-dark, 0.3);
    box-shadow: 0 $space-1 $space-6 rgba($black, 0.5), 0 0 $space-8 rgba($node-org-dark, 0.3);
  }

  .tooltip-title {
    font-weight: 600;
    font-size: $font-size-sm;
    margin-bottom: $space-2;
    padding-bottom: $space-1;
    border-bottom: $border-width-thin solid $gray-200;
    color: $info-500;
  }

  &.dark-theme .tooltip-title {
    border-bottom-color: rgba($node-org-dark, 0.25);
    color: $node-org-dark;
  }

  .tooltip-list {
    margin: 0;
    padding-left: $space-4;
    list-style-type: disc;

    li {
      font-size: $font-size-sm;
      line-height: 1.7;
      padding: $space-0-5 0;
      color: inherit;
    }
  }
}

// ============================================================================
// TTP Section Styles - Light mode
// ============================================================================
#app .cve-info-panel .ttp-section {
  background: $white;
  border-color: $gray-200;
}

#app .cve-info-panel .ttp-detail-item {
  padding: $space-3;
  background: $gray-50;
  border-radius: $radius-lg;
  margin-bottom: $space-2;
  border: $border-width-thin solid $gray-200;

  &:last-child {
    margin-bottom: 0;
  }
}

#app .cve-info-panel.dark-theme .ttp-section {
  background: rgba($node-org-dark, 0.08);
  border-color: rgba($node-org-dark, 0.25);
  box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.2);
}

#app .cve-info-panel.dark-theme .ttp-detail-item {
  background: rgba($node-org-dark, 0.1);
  border-color: rgba($node-org-dark, 0.25);
  box-shadow: 0 0 $space-3 rgba($node-org-dark, 0.15);
}

#app .cve-info-panel .ttp-header {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-bottom: $space-2;
}

#app .cve-info-panel .ttp-id {
  font-family: 'SF Mono', monospace;
  font-size: $font-size-sm;
  font-weight: 600;
  color: $info-500;
  background: $info-50;
  padding: $space-0-5 $space-1;
  border-radius: $radius-md;
}

#app .cve-info-panel.dark-theme .ttp-id {
  color: $node-org-dark;
  background: rgba($node-org-dark, 0.2);
  box-shadow: 0 0 $space-2 rgba($node-org-dark, 0.4);
}

#app .cve-info-panel .ttp-name {
  font-size: $font-size-base;
  color: $gray-900;
  font-weight: 600;
}

#app .cve-info-panel.dark-theme .ttp-name {
  color: $dark-text-primary;
}

#app .cve-info-panel .ttp-row {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-bottom: $space-1;
  font-size: $font-size-sm;
}

#app .cve-info-panel .ttp-row-label {
  color: $gray-700;
  font-weight: 600;
  min-width: 100px;
  font-size: $font-size-sm;
}

#app .cve-info-panel.dark-theme .ttp-row-label {
  color: $dark-text-secondary;
}

#app .cve-info-panel .ttp-tactics {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
}

#app .cve-info-panel .tactic-chip {
  font-size: $font-size-sm; // Increased from xs
  height: $space-5;
  background: $info-50;
  color: $info-500;
}

#app .cve-info-panel.dark-theme .tactic-chip {
  background: rgba($node-org-dark, 0.2);
  color: $node-org-dark;
  box-shadow: 0 0 $space-1 rgba($node-org-dark, 0.3);
}

#app .cve-info-panel .maturity-badge {
  font-size: $font-size-sm; // Increased from xs
  padding: $space-0-5 $space-2;
  border-radius: $radius-lg;
  font-weight: 500;
  background: $info-50;
  color: $info-500;
}

#app .cve-info-panel.dark-theme .maturity-badge {
  background: rgba($node-org-dark, 0.2);
  color: $node-org-dark;
}

#app .cve-info-panel .years-badge,
#app .cve-info-panel .date-text {
  font-size: $font-size-sm;
  color: $gray-700;
  font-weight: 500;
}

#app .cve-info-panel.dark-theme .years-badge,
#app .cve-info-panel.dark-theme .date-text {
  color: $dark-text-secondary;
}

#app .cve-info-panel .ttp-description {
  font-size: $font-size-sm;
  color: $gray-700;
  line-height: $line-height-relaxed;
  padding: $space-2 $space-3;
  background: $gray-100;
  border-radius: $radius-md;
  margin-bottom: $space-2;
  border-left: 3px solid $info-500;

  &.no-data {
    color: $gray-500;
    font-style: italic;
    border-left-color: $gray-300;
  }
}

#app .cve-info-panel.dark-theme .ttp-description {
  background: rgba($node-org-dark, 0.08);
  color: $dark-text-secondary;
  border-left-color: $node-org-dark;
}

#app .cve-info-panel .ttp-d3fends {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
}

#app .cve-info-panel .d3fend-chip {
  font-size: $font-size-sm; // Increased from xs
  height: $space-5;
  background: $info-50;
  color: $info-500;
  border-color: rgba($info-500, 0.25);
}

#app .cve-info-panel.dark-theme .d3fend-chip {
  background: rgba($node-org-dark, 0.15);
  color: $node-org-dark;
  border-color: rgba($node-org-dark, 0.3);
  box-shadow: 0 0 $space-1 rgba($node-org-dark, 0.25);
}

#app .cve-info-panel .more-ttps {
  text-align: center;
  font-size: $font-size-sm; // Increased from xs
  color: $gray-600;
  font-style: italic;
  padding-top: $space-2;
}

#app .cve-info-panel.dark-theme .more-ttps {
  color: $dark-text-secondary;
}

// ============================================================================
// APT Section Styles - Light mode
// ============================================================================
#app .cve-info-panel .apt-section {
  background: $white;
  border-color: $gray-200;
}

#app .cve-info-panel .apt-detail-item {
  padding: $space-3;
  background: $gray-50;
  border-radius: $radius-lg;
  margin-bottom: $space-2;
  border: $border-width-thin solid $gray-200;

  &:last-child {
    margin-bottom: 0;
  }
}

#app .cve-info-panel.dark-theme .apt-section {
  background: rgba($node-org-dark, 0.08);
  border-color: rgba($node-org-dark, 0.25);
  box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.2);
}

#app .cve-info-panel.dark-theme .apt-detail-item {
  background: rgba($node-org-dark, 0.1);
  border-color: rgba($node-org-dark, 0.25);
  box-shadow: 0 0 $space-3 rgba($node-org-dark, 0.15);
}

#app .cve-info-panel .apt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: $space-2;
}

#app .cve-info-panel .apt-name {
  display: flex;
  align-items: center;
  gap: $space-2;
  font-size: $font-size-base;
  font-weight: 600;
  color: $gray-900;
}

#app .cve-info-panel.dark-theme .apt-name {
  color: $dark-text-primary;
}

#app .cve-info-panel .apt-color {
  width: $space-2;
  height: $space-2;
  border-radius: $radius-full;
  flex-shrink: 0;
  background: $info-500;
}

#app .cve-info-panel .apt-id {
  font-size: $font-size-sm;
  color: $gray-600;
  font-weight: normal;
}

#app .cve-info-panel.dark-theme .apt-id {
  color: $dark-text-secondary;
}

#app .cve-info-panel .apt-status {
  font-size: $font-size-sm; // Increased from xs
  padding: $space-0-5 $space-2;
  border-radius: $radius-lg;
  background: $info-50;
  color: $info-500;
}

#app .cve-info-panel.dark-theme .apt-status {
  background: rgba($node-org-dark, 0.2);
  color: $node-org-dark;
  box-shadow: 0 0 $space-2 rgba($node-org-dark, 0.4);
}

#app .cve-info-panel .apt-row {
  display: flex;
  align-items: center;
  gap: $space-2;
  margin-bottom: $space-1;
  font-size: $font-size-sm;
}

#app .cve-info-panel .apt-row-label {
  color: $gray-700;
  font-weight: 600;
  min-width: 120px;
  font-size: $font-size-sm;
}

#app .cve-info-panel.dark-theme .apt-row-label {
  color: $dark-text-secondary;
}

#app .cve-info-panel .recent-updates-value {
  color: $gray-900;
  font-weight: 500;
}

#app .cve-info-panel.dark-theme .recent-updates-value {
  color: $dark-text-primary;
}

#app .cve-info-panel .alias-list {
  font-size: $font-size-sm;
  color: $gray-700;
  font-style: italic;
}

#app .cve-info-panel.dark-theme .alias-list {
  color: $dark-text-secondary;
}

#app .cve-info-panel .apt-description {
  font-size: $font-size-sm;
  color: $gray-700;
  line-height: $line-height-relaxed;
  padding: $space-2 $space-3;
  background: $gray-100;
  border-radius: $radius-md;
  margin-bottom: $space-2;
  border-left: 3px solid $info-500;

  &.no-data {
    color: $gray-500;
    font-style: italic;
    border-left-color: $gray-300;
  }
}

#app .cve-info-panel.dark-theme .apt-description {
  background: rgba($node-org-dark, 0.08);
  color: $dark-text-secondary;
  border-left-color: $node-org-dark;
}

#app .cve-info-panel .apt-stats-row {
  display: flex;
  justify-content: space-around;
  gap: $space-4; // Add spacing between stat items
  padding: $space-2;
  background: $gray-100;
  border-radius: $radius-lg;
  margin: $space-2 0;
}

#app .cve-info-panel.dark-theme .apt-stats-row {
  background: rgba($node-org-dark, 0.12);
  box-shadow: inset 0 0 $space-5 rgba($node-org-dark, 0.15);
}

#app .cve-info-panel .apt-stat {
  text-align: center;
  flex: 1; // Equal width for all stats
  min-width: 0; // Allow text to wrap if needed
  padding: $space-1; // Add padding for better hover area
  border-radius: $radius-md; // Subtle rounded corners
  transition: background-color 0.2s ease; // Smooth hover effect
  &:hover {
    background-color: rgba($info-500, 0.05); // Subtle highlight on hover
  }
}

#app .cve-info-panel .apt-stat-value {
  display: block;
  font-size: $font-size-lg;
  font-weight: 700;
  color: $gray-900;
}

#app .cve-info-panel.dark-theme .apt-stat-value {
  color: $dark-text-primary;
}

#app .cve-info-panel .apt-stat-label {
  display: block; // Force label to be on its own line
  font-size: $font-size-xs; // Reduced back to xs for better differentiation
  color: $gray-600;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: $space-1; // Add space between value and label
  line-height: 1.2; // Tighter line height for multi-word labels
}

#app .cve-info-panel.dark-theme .apt-stat-label {
  color: $dark-text-secondary;
}

#app .cve-info-panel .rank-badge {
  font-size: $font-size-sm; // Increased from xs
  font-weight: 600;
  color: $info-500;
  background: $info-50;
  padding: $space-0-5 $space-2;
  border-radius: $radius-md;
}

#app .cve-info-panel.dark-theme .rank-badge {
  color: $node-org-dark;
  background: rgba($node-org-dark, 0.2);
}

#app .cve-info-panel .apt-targeting {
  margin-top: $space-2;
  display: flex;
  align-items: flex-start;
  gap: $space-2;
}

#app .cve-info-panel .targeting-label {
  font-size: $font-size-sm;
  color: $gray-700;
  font-weight: 600;
  min-width: 100px;
  flex-shrink: 0;
}

#app .cve-info-panel.dark-theme .targeting-label {
  color: $dark-text-secondary;
}

#app .cve-info-panel .targeting-chips {
  display: flex;
  flex-wrap: wrap;
  gap: $space-1;
}

#app .cve-info-panel .sector-chip,
#app .cve-info-panel .region-chip {
  font-size: $font-size-sm; // Increased from xs
  height: $space-5;
  background: $info-50;
  color: $info-500;
}

#app .cve-info-panel.dark-theme .sector-chip,
#app .cve-info-panel.dark-theme .region-chip {
  background: rgba($node-org-dark, 0.15);
  color: $node-org-dark;
  box-shadow: 0 0 $space-1 rgba($node-org-dark, 0.25);
}

// ============================================================================
// Stats Section - Light mode
// ============================================================================
#app .cve-info-panel .stats-section {
  display: flex;
  justify-content: space-around;
  text-align: center;
  background: $gray-100;
  border: none;
}

#app .cve-info-panel .stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

#app .cve-info-panel .stat-value {
  font-size: $font-size-3xl;
  font-weight: 700;
  color: $gray-900;
}

#app .cve-info-panel .stat-label {
  font-size: $font-size-sm; // Increased from xs
  color: $gray-600;
  text-transform: uppercase;
}

#app .cve-info-panel.dark-theme .stats-section {
  background: rgba($node-org-dark, 0.12);
  box-shadow: 0 0 $space-8 rgba($node-org-dark, 0.2);
}

#app .cve-info-panel.dark-theme .stat-value {
  color: $dark-text-primary;
}

#app .cve-info-panel.dark-theme .stat-label {
  color: $dark-text-secondary;
}

// ============================================================================
// IoC Section - Dark mode (lock icon orange glow)
// ============================================================================
#app .cve-info-panel.dark-theme .ioc-section {
  background: rgba($node-org-dark, 0.08);
  border-color: rgba($node-org-dark, 0.25);
  box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.2);
}

#app .cve-info-panel.dark-theme .ioc-placeholder {
  background: rgba($node-org-dark, 0.08);
  border-color: rgba($node-org-dark, 0.25);
}

#app .cve-info-panel.dark-theme .ioc-placeholder-icon {
  color: $warning-500;
  opacity: 1;
  filter: drop-shadow(0 0 $space-3 rgba($warning-500, 0.8)) drop-shadow(0 0 $space-6 rgba($warning-500, 0.5));
}

#app .cve-info-panel.dark-theme .ioc-placeholder-title {
  color: $dark-text-primary;
}

#app .cve-info-panel.dark-theme .ioc-placeholder-text {
  color: $dark-text-secondary;
}

// ============================================================================
// Collapsible header - full width and height hover
// ============================================================================
#app .cve-info-panel .collapsible-header {
  margin: -$space-3;
  padding: $space-3;
  min-height: $space-12;
  display: flex;
  align-items: center;
  border-radius: $radius-lg;
}

#app .cve-info-panel.dark-theme .collapsible-header:hover {
  background: rgba($node-org-dark, 0.15);
  box-shadow: 0 0 $space-6 rgba($node-org-dark, 0.3), inset 0 0 $space-4 rgba($node-org-dark, 0.1);
}

#app .cve-info-panel.dark-theme .collapsible-header .v-icon {
  color: $node-org-dark;
}

#app .cve-info-panel.dark-theme .collapse-icon {
  color: $node-org-dark;
}

// Close button - ensure circular shape
#app .cve-info-panel .info-panel__header .v-btn.v-btn--icon {
  border-radius: 50%;
  width: 24px;
  height: 24px;

  &::before {
    border-radius: 50%;
  }

  .v-icon {
    font-size: 16px;
  }
}

#app .cve-info-panel.dark-theme .info-panel__header .v-btn.v-btn--icon {
  color: $dark-text-secondary;
  background: transparent;

  &:hover {
    background: rgba($node-org-dark, 0.15);
    color: $node-org-dark;
  }

  .v-icon {
    color: inherit;
  }
}

// ============================================================================
// Tooltip text wrapping and overflow fix
// ============================================================================
.v-tooltip__content {
  max-width: 400px !important;
  word-wrap: break-word !important;
  word-break: break-word !important;
  white-space: normal !important;
  overflow-wrap: break-word !important;
  line-height: 1.5 !important;
  padding: $space-2 $space-3 !important;
}
</style>
